import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowNode,
} from '@ag-grid-community/core';
import { MenuItem, useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import {
  Button,
  Heading,
  Icon,
  IconButton,
  Status,
} from 'copilot-design-system';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { BaseActionsMenuProps, DATE_FORMAT } from 'src/constants';
import AgGrid, {
  PriceActionRenderer,
  ProductPriceTableRowModal,
} from 'src/legacy/components/AgGrid';
import {
  GetCurrencyPrefix,
  StripeCurrencyFormatterAdapter,
} from 'src/legacy/components/Formatters';
import { priceSchema } from 'src/legacy/components/Products/ProductDetailPage/AddPrice';
import { getRenderCurrencyPrefixProps } from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import {
  BaseTextField,
  CurrencyTextField,
} from 'src/legacy/components/TextField';
import { LineItem } from 'src/services/api/invoicesApi';
import {
  BILLING_PERIODS,
  Price,
  PriceType,
  useAddPriceByProductIdMutation,
  useDeletePriceMutation,
} from 'src/services/api/productsApi';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { DateUtils } from 'src/utils';

const ONE_TIME_PAYMENT = 'One-time payment';
const ONE_TIME = 'oneTime';

// Price Cell Renderer
const PriceCellRenderer = (params: ICellRendererParams) => {
  const mode = params.colDef?.cellRendererParams.mode;
  const defaultCurrency =
    params.colDef?.cellRendererParams?.defaultCurrency || '';
  const productId =
    params.colDef?.cellRendererParams?.productId || params.data.productId || '';

  if (!params.data.isNew) {
    return StripeCurrencyFormatterAdapter({
      value:
        typeof params.data.amount === 'number'
          ? params.data.amount
          : params.data.amount * 100,
      currency: defaultCurrency,
    });
  }

  const prefix = GetCurrencyPrefix({ currency: defaultCurrency });

  return (
    <Formik
      initialValues={{
        amount: params.data.amount ?? '',
        currency: params.data.currency ?? '',
        productId: productId,
        type: mode === 'OneTimeStore' ? PriceType.OneTime : PriceType.Recurring,
        interval: params.data.interval ?? SubscriptionInterval.Month,
      }}
      validationSchema={toFormikValidationSchema(priceSchema)}
      onSubmit={(values) => {
        params.node.setDataValue('amount', values.amount);
        params.node.setDataValue('currency', values.currency);
        params.node.setDataValue('interval', values.interval);
      }}
      enableReinitialize
    >
      {({ values, errors, handleChange, handleBlur }) => {
        params.colDef?.cellRendererParams?.setPriceError(
          errors ? errors.amount : null,
        );
        return (
          <Form>
            <CurrencyTextField
              name="amount"
              variant="outlined"
              fullWidth
              onBlur={handleBlur}
              onChange={(e) => {
                handleChange(e);
                params.node.setDataValue('amount', e.target.value);
              }}
              autoFocus
              InputProps={getRenderCurrencyPrefixProps(prefix || '')}
              value={values.amount}
              error={Boolean(errors.amount)}
              currency={defaultCurrency}
              className="[&>div>input]:!text-base [&>div>input]:!h-8 [&>div>input]:!font-normal [&>div>input]:!text-black-heading"
            />
          </Form>
        );
      }}
    </Formik>
  );
};

// Billing Period Cell Renderer
const BillingPeriodCellRenderer = (params: ICellRendererParams) => {
  const mode = params.colDef?.cellRendererParams.mode;
  const selectedInterval = params.colDef?.cellRendererParams.selectedInterval;

  if (!params.data.isNew) {
    return (
      BILLING_PERIODS.find((x) => x.value === params.data.interval)?.label ||
      '—'
    );
  }

  // Set initial interval value based on mode
  const initialInterval =
    mode === 'All' ? ONE_TIME : (params.data.interval ?? '');

  if (mode === 'OneTimeStore') return null;

  return (
    <Formik
      initialValues={{
        interval: selectedInterval ?? initialInterval,
      }}
      onSubmit={() => {}}
      enableReinitialize
    >
      {({ values, handleChange }) => (
        <Form>
          <BaseTextField
            select
            variant="outlined"
            name="interval"
            value={values.interval}
            disabled={Boolean(selectedInterval)}
            onChange={(e) => {
              handleChange(e);
              params.node.setDataValue('interval', e.target.value);
            }}
            additionalSelectProps={{
              classes: {
                root: 'text-base text-black-heading font-normal !h-8 overflow-hidden text-ellipsis whitespace-nowrap truncate',
                disabled: 'text-hover-border',
              },
            }}
            className={clsx(
              '[&>div>svg]:h-4 [&>div>svg]:w-4 [&>div>svg]:mt-1.5 w-[90px] @xs:w-[130px] @sm:w-[160px]',
              {
                '[&>fieldset]:!border-non-hover-border':
                  Boolean(selectedInterval),
              },
            )}
          >
            {mode === 'All' && (
              <MenuItem
                className="border-b border-solid border-secondary-hover"
                value={ONE_TIME}
              >
                <span className="text-ellipsis whitespace-nowrap overflow-hidden block">
                  {ONE_TIME_PAYMENT}
                </span>
              </MenuItem>
            )}

            {BILLING_PERIODS.map((period) => (
              <MenuItem value={period.value} key={period.value}>
                {period.label}
              </MenuItem>
            ))}
          </BaseTextField>
        </Form>
      )}
    </Formik>
  );
};

// Updated Price Action Renderer
interface ExtendedCellRendererParams extends ICellRendererParams {
  handleSave: (node: RowNode) => void;
  handleDiscard: (node: RowNode) => void;
  handleClickRemove: (data: ProductPriceTableRowModal) => void;
  isDeleteDisabled: (data: ProductPriceTableRowModal) => boolean;
  onActionMenuClick: (action: BaseActionsMenuProps) => void;
  iSavePriceLoading: boolean;
  isSmallViewPort: boolean;
  priceError: string;
  lineItems: LineItem[];
  mode: string;
  selectedPriceId: string;
  onSetPriceId: (priceId: string) => void;
  selectedInterval: string;
}

const PriceActionsRenderer = (params: ExtendedCellRendererParams) => {
  const isNewRow = params.data.isNew;
  const theme = useTheme();
  const isExtraSmallViewPort = useMediaQuery(theme.breakpoints.down('xs'));
  if (isNewRow) {
    const isSaveDisabled =
      params.data.amount <= 0 || !params.data.interval || !!params.priceError;
    return (
      <div className="flex justify-end items-center gap-2">
        {isExtraSmallViewPort ? (
          <>
            <IconButton
              variant="secondary"
              label="Discard"
              onClick={() => params.handleDiscard(params.node)}
              icon="Close"
            />
            <IconButton
              variant="primary"
              label="Save"
              icon="Check"
              onClick={() => params.handleSave(params.node)}
              disabled={isSaveDisabled}
            />
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              label="Discard"
              prefixIcon="Close"
              onClick={() => params.handleDiscard(params.node)}
              disabled={params.iSavePriceLoading}
              className="[&>svg]:box-content"
            />
            <Button
              variant="primary"
              label="Save"
              prefixIcon="Check"
              disabled={isSaveDisabled}
              onClick={() => params.handleSave(params.node)}
              loading={params.iSavePriceLoading}
              className="[&>svg]:box-content"
            />
          </>
        )}
      </div>
    );
  } else {
    const {
      mode,
      selectedInterval,
      selectedPriceId,
      lineItems,
      data,
      onSetPriceId,
      onActionMenuClick,
      handleClickRemove,
    } = params;
    const isDeleteDisabled = params.isDeleteDisabled(data);
    const isPriceUsed = lineItems.some((li) => li?.priceId === params.data.id);
    const showCurrentPriceBadge = selectedPriceId === params.data.id;
    // Updated logic for enabling "Set as current price"
    let isEnabledSetCurrentPriceAction = false;

    if (mode === 'OneTimeStore') {
      // Store One-time purchases: enable only when no interval is selected AND the price is not already used.
      isEnabledSetCurrentPriceAction = !data.interval && !isPriceUsed;
    } else if (mode === 'subscriptionsStore') {
      // Store Plans: enable only when the interval exists AND the price is not already used.
      isEnabledSetCurrentPriceAction = data.interval && !isPriceUsed;
    } else if (mode === 'All') {
      // PaymentLink : allow setting the current price for all type of prices.
      isEnabledSetCurrentPriceAction = true;
    } else {
      // If more than one line item exists → interval must match the selected interval.
      // If only one line item exists → always allow.
      isEnabledSetCurrentPriceAction =
        lineItems.length > 1 ? data.interval === selectedInterval : true;
    }

    return (
      <div className="flex items-center gap-2">
        {showCurrentPriceBadge && (
          <Status label="Current price" status="neutral" />
        )}
        <PriceActionRenderer
          isDeleteDisabled={isDeleteDisabled}
          isPriceUsed={isPriceUsed}
          mode={params.mode}
          handleClickRemove={() => handleClickRemove(data)}
          data={data}
          entityName={'price'}
          onActionMenuClick={onActionMenuClick}
          {...(!showCurrentPriceBadge
            ? {
                onCurrentPriceAction: {
                  isDisabled: !isEnabledSetCurrentPriceAction,
                  name: 'Set as current price',
                  onClick: () => onSetPriceId(data.id),
                  icon: <Icon name="Check" icon="Check" className="h-3 w-3" />,
                },
              }
            : {})}
        />
      </div>
    );
  }
};

export const PriceTable = ({
  productId,
  defaultCurrency,
  mode,
  prices = [],
  isAddingNewPrice = false,
  onAddNewPrice,
  lineItems,
  onSetPriceId,
  selectedInterval,
  selectedPriceId,
}: {
  productId: string;
  defaultCurrency: string;
  mode: string;
  prices: Price[];
  isAddingNewPrice: boolean;
  onAddNewPrice: (isAddingNewPrice: boolean) => void;
  lineItems?: LineItem[];
  onSetPriceId?: (priceId: string) => void;
  selectedInterval?: string;
  selectedPriceId?: string;
}) => {
  const [deletePrice] = useDeletePriceMutation();
  const [addPrice, { isLoading: iSavePriceLoading }] =
    useAddPriceByProductIdMutation();

  const theme = useTheme();
  const [priceError, setPriceError] = useState<string | null>(null);

  const isExtraSmallViewPort = useMediaQuery(theme.breakpoints.down('xs'));

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const rowData = useMemo(() => {
    if (!prices) return [];
    const sortedPrices = [...prices].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    return sortedPrices.map((p) => ({
      id: p.id,
      amount: p.amount,
      currency: p.currency,
      interval: p.interval,
      created: DateUtils.formatDate(p?.createdAt, DATE_FORMAT),
      isUsed: p.isUsed,
      isNew: false,
    }));
  }, [prices]);

  const handleAddPrice = () => {
    if (!gridApi || isAddingNewPrice) return;
    onAddNewPrice(true);
    const isOneTimeMode = mode === 'OneTimeStore' || mode === 'All';
    const newRow = {
      id: null,
      amount: '',
      currency: defaultCurrency,

      ...(selectedInterval
        ? // For subscription users: once a price interval (e.g., monthly) is selected for the first product,
          // all subsequently added prices must use the same interval.
          { interval: selectedInterval }
        : !isOneTimeMode
          ? { interval: SubscriptionInterval.Month }
          : { interval: ONE_TIME }),
      created: '',
      isUsed: false,
      isNew: true,
      type: isOneTimeMode ? PriceType.OneTime : PriceType.Recurring,
    };
    gridApi.applyTransaction({ add: [newRow], addIndex: 0 });
  };

  const handlePriceRemove = (data: ProductPriceTableRowModal) => {
    if (!productId || !data.id) return;
    deletePrice({ productId, priceId: data.id });
  };

  const handleSaveNewPrice = async (rowNode: RowNode) => {
    if (!productId) return;
    const { data } = rowNode;
    const isOneTime = data.interval === ONE_TIME || mode === 'OneTimeStore';
    try {
      const response = await addPrice({
        productId,
        amount: data.amount.replace(/,/g, ''),
        currency: data.currency,
        type: isOneTime ? PriceType.OneTime : PriceType.Recurring,
        ...(isOneTime ? {} : { interval: data.interval }),
      }).unwrap();

      rowNode.setData({
        ...data,
        id: response.id,
        created: DateUtils.formatDate(new Date(), DATE_FORMAT),
        isNew: false,
      });
      onAddNewPrice(false);
      setPriceError(null);

      // Notify parent about the new price
      if (onSetPriceId) {
        onSetPriceId(response.id);
      }
    } catch (error) {
      onAddNewPrice(false);
    }
  };

  const handleDiscard = (rowNode: RowNode) => {
    if (!gridApi) return;
    gridApi.applyTransaction({ remove: [rowNode.data] });
    onAddNewPrice(false);
    setPriceError(null);
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Price',
        field: 'amount',

        maxWidth: isExtraSmallViewPort ? 80 : 112,
        minWidth: isExtraSmallViewPort ? 80 : 112,
        cellRenderer: 'priceCellRenderer',
        headerClass: 'text-left !pl-2',
        cellClass: '!pl-2 !pr-3',
        suppressSizeToFit: true,
        cellRendererParams: { defaultCurrency, productId, setPriceError },
      },

      {
        headerName: 'Period',
        field: 'interval',
        headerClass: 'text-left !pl-2',
        minWidth: isExtraSmallViewPort ? 160 : 200,
        cellClass: '!px-3',
        cellRenderer: 'billingPeriodCellRenderer',
        cellRendererParams: {
          mode,
          selectedInterval,
          lineItems,
        },
      },

      {
        headerName: 'Actions',
        suppressSizeToFit: true,
        pinned: 'right',
        cellClass: '!flex justify-end items-center !pr-2',
        maxWidth: isExtraSmallViewPort ? 50 : 200,

        cellRenderer: 'priceRenderer',
        cellRendererParams: {
          handleClickRemove: handlePriceRemove,
          handleSave: handleSaveNewPrice,
          handleDiscard: handleDiscard,
          entityName: 'price',
          isDeleteDisabled: (data: ProductPriceTableRowModal) =>
            prices?.length === 1 || !data.id,
          iSavePriceLoading,
          priceError,
          lineItems,
          mode,
          selectedPriceId,
          onSetPriceId,
          selectedInterval,
        },
      },
    ],
    [
      isExtraSmallViewPort,
      defaultCurrency,
      productId,
      mode,
      handlePriceRemove,
      handleSaveNewPrice,
      handleDiscard,
      iSavePriceLoading,
      priceError,
      prices,
      selectedInterval,
      isAddingNewPrice,
      selectedPriceId,
      lineItems,
    ],
  );

  return (
    <div className="flex flex-col gap-3">
      <div
        className={clsx('flex items-center justify-between', {
          'opacity-25 pointer-events-none': isAddingNewPrice,
        })}
      >
        <Heading size="lg">Pricing</Heading>
        <Button
          variant="secondary"
          label=" Add a price"
          prefixIcon="Plus"
          onClick={handleAddPrice}
          disabled={isAddingNewPrice}
        />
      </div>
      <div className="uniform-table-body-height @container">
        <AgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          onCreateNewRow={(params: GridReadyEvent) => setGridApi(params.api)}
          frameworkComponents={{
            priceRenderer: PriceActionsRenderer,
            priceCellRenderer: PriceCellRenderer, // Use direct reference
            billingPeriodCellRenderer: BillingPeriodCellRenderer,
          }}
        />
      </div>
      {priceError && <ErrorMessage message={priceError} />}
    </div>
  );
};
